import React, { useEffect, useState } from "react";
import axios from "axios";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { useI18n } from "@/lib/i18n";
import { Activity, CheckCircle2, AlertTriangle, XCircle, Users, FolderTree, Database, TrendingUp } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function bytesToHuman(b) {
  if (!b) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0; let v = b;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 ? 2 : v < 100 ? 1 : 0)} ${units[i]}`;
}

const STATUS_LABEL = {
  fr: {
    operational: "Tous les systèmes opérationnels",
    degraded: "Performance dégradée",
    outage: "Incident en cours",
    today: "Aujourd'hui",
    last7days: "7 derniers jours",
    last30days: "30 derniers jours",
    runs: "Sauvegardes",
    bytes: "Données transférées",
    files: "Fichiers copiés",
    users: "Utilisateurs",
    accounts: "Comptes cloud connectés",
    activeJobs: "Sauvegardes actives",
    success: "Taux de succès",
    timeline: "Activité — 30 derniers jours",
    successful: "Réussies",
    failed: "Échouées",
    refreshed: "Mis à jour",
  },
  en: {
    operational: "All systems operational",
    degraded: "Degraded performance",
    outage: "Ongoing incident",
    today: "Today",
    last7days: "Last 7 days",
    last30days: "Last 30 days",
    runs: "Backups",
    bytes: "Data transferred",
    files: "Files copied",
    users: "Users",
    accounts: "Connected cloud accounts",
    activeJobs: "Active backups",
    success: "Success rate",
    timeline: "Activity — Last 30 days",
    successful: "Successful",
    failed: "Failed",
    refreshed: "Updated",
  },
};

export default function Status() {
  const { lang } = useI18n();
  const L = STATUS_LABEL[lang] || STATUS_LABEL.fr;
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = () => {
    axios.get(`${BACKEND}/api/status`).then((r) => setData(r.data)).catch(() => setErr(true));
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000); // auto refresh every 30s
    return () => clearInterval(t);
  }, []);

  const statusCfg = {
    operational: { Icon: CheckCircle2, color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-500" },
    degraded: { Icon: AlertTriangle, color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-500" },
    outage: { Icon: XCircle, color: "text-red-600", bg: "bg-red-50", border: "border-red-500" },
  }[data?.status || "operational"];

  return (
    <div className="App min-h-screen bg-background" data-testid="status-page">
      <Header />
      <main className="mx-auto max-w-5xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="font-mono text-xs uppercase tracking-wider text-[#002FA7]">// {data?.branding?.app_name || "SAVEBOX"} · status</div>
        <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">Status</h1>
        <p className="mt-2 text-sm text-muted-foreground">{lang === "en" ? "Real-time service health and activity." : "État du service et activité en temps réel."}</p>

        {err && (
          <div className="mt-6 border border-red-500 bg-red-50 p-4 text-sm text-red-800">Unable to load status data.</div>
        )}

        {data && (
          <>
            {/* Big status banner */}
            <div className={`mt-6 flex items-center gap-3 border ${statusCfg.border} ${statusCfg.bg} p-5`} data-testid="status-banner">
              <statusCfg.Icon className={`h-8 w-8 ${statusCfg.color}`} />
              <div>
                <div className={`font-display text-xl font-bold ${statusCfg.color}`}>{L[data.status]}</div>
                <div className="mt-0.5 font-mono text-[11px] text-muted-foreground">
                  {L.refreshed} {new Date(data.server_time).toLocaleString(lang === "fr" ? "fr-FR" : "en-US")}
                </div>
              </div>
            </div>

            {/* Today + Week */}
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KpiCard label={L.today + " — " + L.runs} value={data.today.runs} sub={`${data.today.successful} ${L.successful} · ${data.today.failed} ${L.failed}`} Icon={Activity} testid="kpi-today-runs" />
              <KpiCard label={L.today + " — " + L.bytes} value={bytesToHuman(data.today.bytes)} Icon={Database} testid="kpi-today-bytes" />
              <KpiCard label={L.last7days + " — " + L.runs} value={data.week.runs} sub={`${data.week.files} ${L.files.toLowerCase()}`} Icon={TrendingUp} testid="kpi-week-runs" />
              <KpiCard label={L.last30days + " — " + L.success} value={`${data.success_rate_30d}%`} Icon={CheckCircle2} testid="kpi-success-rate" />
            </div>

            {/* Totals */}
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
              <KpiCard label={L.users} value={data.totals.users} Icon={Users} testid="kpi-users" />
              <KpiCard label={L.accounts} value={data.totals.drive_accounts} Icon={FolderTree} testid="kpi-accounts" />
              <KpiCard label={L.activeJobs} value={data.totals.jobs_active} Icon={Activity} testid="kpi-jobs" />
            </div>

            {/* Timeline */}
            <div className="mt-10 border border-border bg-card p-5">
              <div className="font-mono text-xs uppercase text-muted-foreground">// {L.timeline}</div>
              <Timeline timeline={data.timeline || []} />
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

function KpiCard({ label, value, sub, Icon, testid }) {
  return (
    <div className="border border-border bg-card p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
        <Icon className="h-3.5 w-3.5 text-[#002FA7]" />
      </div>
      <div className="mt-2 font-display text-2xl font-black tracking-tight">{value}</div>
      {sub && <div className="mt-1 font-mono text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function Timeline({ timeline }) {
  // Build a continuous 30-day series, filling missing dates with zeros
  const map = {};
  for (const t of timeline) map[t.date] = t;
  const days = [];
  const today = new Date();
  for (let i = 29; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    const entry = map[key] || { count: 0, ok: 0, bytes: 0 };
    days.push({ date: key, ...entry });
  }
  const maxCount = Math.max(1, ...days.map((d) => d.count));

  return (
    <div className="mt-4">
      <div className="flex h-32 items-end gap-1" data-testid="timeline-bars">
        {days.map((d) => {
          const h = (d.count / maxCount) * 100;
          const allOk = d.count > 0 && d.ok === d.count;
          const someFail = d.count > 0 && d.ok < d.count;
          const bg = d.count === 0 ? "bg-border" : allOk ? "bg-emerald-500" : someFail ? "bg-amber-500" : "bg-red-500";
          return (
            <div
              key={d.date}
              className={`flex-1 ${bg} transition-all hover:opacity-80`}
              style={{ height: d.count === 0 ? "4px" : `${Math.max(h, 8)}%` }}
              title={`${d.date} · ${d.count} runs · ${d.ok} ok`}
              data-testid={`timeline-day-${d.date}`}
            />
          );
        })}
      </div>
      <div className="mt-2 flex justify-between font-mono text-[10px] text-muted-foreground">
        <span>-30j</span>
        <span>{new Date().toISOString().slice(0, 10)}</span>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-4 font-mono text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2 w-2 bg-emerald-500" /> 100% OK</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2 w-2 bg-amber-500" /> partiel</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2 w-2 bg-red-500" /> échec</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2 w-2 bg-border" /> aucun run</span>
      </div>
    </div>
  );
}
